<template>
    <v-container class="pa-0" grid-list-md>
        <v-layout row align-center justify-center>
            <v-flex xs2>
                <v-autocomplete
                    label="Plant"
                    :items="plants"
                    :value="selectedPlantID"
                    @input="handlePlantSelected"
                    hide-details
                    dense
                    outline
                    :read-only="plants.length == 1">
                </v-autocomplete>
            </v-flex>
            <v-flex xs2 v-if="plantSelected">
                <v-autocomplete
                    label="Category"
                    :items="categories"
                    :value="selectedCategoryIDs"
                    @input="handleCategorySelected"
                    hide-details
                    dense
                    :menu-props="{closeOnContentClick:true}"
                    outline
                    multiple
                    :read-only="categories.length == 1">
                </v-autocomplete>
            </v-flex>
            <v-flex xs2 v-if="plantSelected && categorySelected">
                <FinancialCalendarWeekPicker 
                    :value="selectedDate"
                    @input="onDateSelected">
                </FinancialCalendarWeekPicker>
            </v-flex>
            <v-flex xs2 class="px-1" v-if="canSelectUOM">
                <v-autocomplete                                     
                    label="UOM"
                    :items="$Constants.HARVESTPLAN_UOM"
                    item-value="ID"
                    item-text="Name"
                    :value="selectedUOM"
                    @input="handleUOMSelected"
                    hide-details
                    dense
                    outline>
                </v-autocomplete>
            </v-flex>
            <v-flex shrink v-if="plantSelected && categorySelected">
                <v-btn color="primary" @click="openAddTeamDialog" ref="teamRosterButton">Manage Team Roster</v-btn>
            </v-flex>
            <v-flex shrink>
                <v-checkbox
                    color="primary"
                    dense
                    hide-details
                    class="mt-0"
                    v-model="showPreviousSunday"
                    @change="showPreviousSundayChanged"
                    label="Show Previous Sunday">
                </v-checkbox>
                <v-checkbox
                    color="primary"
                    dense
                    hide-details
                    class="mt-0"
                    v-model="showActuals"
                    @change="showActualsChanged"
                    label="Show Actuals">
                </v-checkbox>
                <v-checkbox
                    color="primary"
                    dense
                    hide-details
                    class="mt-0"
                    v-model="showMultiSelect"
                    @change="showMultiSelectChanged"
                    label="Show Multi Select">
                </v-checkbox>
            </v-flex>
        </v-layout>
        <v-dialog  v-if="addTeamDialog" v-model="addTeamDialog" persistent max-width="400" transition="dialog-bottom-transition" @keydown.esc='closeAddTeamDialog'>
            <v-card>
                <v-toolbar dark color="primary">
                    <v-toolbar-title >Manage Team Roster</v-toolbar-title>
                </v-toolbar>
                <v-card-text>
                    <v-layout wrap>
                        <v-flex xs8>
                            <v-autocomplete
                                label="Team"
                                ref="rosterTeamSelect"
                                class="rosterTeamSelect"
                                :items="unselectedTeams"
                                @input="handleTeamSelected"
                                v-model="tempSelectedTeamIDs"
                                hide-details
                                item-value="TeamID"
                                item-text="TeamName"
                                multiple
                                dense
                                outline>
                                 <template v-slot:item="data">
                                     <TeamNameWithRosteredProductCategories :ref="'team'+data.item.TeamID" :teamID="data.item.TeamID" :teamName="data.item.TeamName"></TeamNameWithRosteredProductCategories>
                                </template>
                            </v-autocomplete>
                        </v-flex>
                        <v-flex shrink>
                            <div class="pt-2">
                                <v-btn color="primary" @click="addTeams" ref="teamRosterButtonAdd" :disabled="tempSelectedTeamIDs.length == 0">
                                    Add
                                </v-btn>
                            </div>
                        </v-flex>
                        <v-flex xs12 class="mt-0">
                            <v-data-table
                                :headers="headers"
                                :items="teamItems"
                                :pagination.sync="pagination"
                                hide-actions
                            >
                            <template v-slot:items="props">
                                <td class="text-xs-center">
                                    <TeamNameWithRosteredProductCategories :teamID="props.item.TeamID" :teamName="props.item.TeamName"></TeamNameWithRosteredProductCategories>
                                </td>
                                <td class="text-xs-center">
                                   <v-btn color="error" icon  :disabled="hasAllocatedVolumeForTeam(props.item.TeamID) || loadingTeamRosterResult" @click="deallocateTeam(props.item.TeamID)">
                                       <v-icon>clear
                                        </v-icon>
                                    </v-btn>
                                </td>
                                </template>
                            </v-data-table>
                        </v-flex>
                    </v-layout> 
                </v-card-text>
                <v-layout>
                    <v-flex xs12 class="pa-2">
                        <v-alert :value="error != null" outline type="error">
                            {{error}}
                        </v-alert>
                    </v-flex>
                </v-layout>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn block color="success" :disabled="loading" :loading="loadingTeamRosterResult" @click="confirmTeamSelection">Confirm</v-btn>
                    <v-btn block color="error" :disabled="loading || loadingTeamRosterResult"  @click="closeAddTeamDialog">Cancel</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import UtilService from '@/services/utilService.js';
import moment from "moment";
import { mapState, mapMutations, mapActions, mapGetters } from "vuex";
import FinancialCalendarWeekPicker from '@/components/berriesRoster/FinancialCalendarWeekPicker.vue'
import RosterUtilService from '@/services/rosterUtilService';
import TeamNameWithRosteredProductCategories from '@/components/berriesRoster/TeamNameWithRosteredProductCategories.vue';
import rosterAPI from "@/api/rosterApi";

export default {
    components: {
        FinancialCalendarWeekPicker,
        TeamNameWithRosteredProductCategories
    },
    props: {
        routePlantID: {
            type: Number,
            required: false
        },
        routeProductCategoryID: {
            type: Number,
            required: false
        },
        loading: {
            type: Boolean,
            required: false
        },
        error: {
            type: String,
            required: false
        }
    },
    data(){
        return {
            loadingTeamRosterResult: false,
            addTeamDialog: false,
            localSelectedTeamIDs: [],
            tempSelectedTeamIDs: [],
            localDeletedTeamIDs: [],
            headers: [
                {
                    text: 'Team Name',
                    value: 'TeamName',
                    class:"text-xs-center"
                },
                {   text: 'Deallocate', 
                    value:" Deallocate",
                    class:"text-xs-center" }
            ],
            pagination: {
                sortBy: 'TeamName',
                rowsPerPage: -1
            },
            showPreviousSunday: false,
            showActuals: false,
            showMultiSelect: false,
        };
    },
    watch: {
        async addTeamDialog() {
            if(this.addTeamDialog) {
                try {
                    this.loadingTeamRosterResult = true;
                    await this.loadTeamRosterResult(this.selectedTeams.map(t=> t.TeamID));

                    // if there are no teams rostered, we want to fetch last weeks rostered teams and just add them to the grid
                    if (!this.selectedTeamIDs || this.selectedTeamIDs.length <= 0) {
                        let teamRosterResult = await await rosterAPI.loadTeamRosterResult(
                            [],
                            moment(this.startOfSelectedWeek).add(-1, "week").format('YYYY-MM-DD'),
                            moment(this.endOfSelectedWeek).add(-1, "week").format('YYYY-MM-DD')
                        );

                        // pretend we selected all of these teams from the list and clicked the "Add" button
                        if (teamRosterResult && teamRosterResult.length > 0) {
                            this.tempSelectedTeamIDs = teamRosterResult
                                .filter(x => x.ProductCategories.find(pc=> this.selectedCategoryIDs.some(spt=> spt == pc.ProductCategoryID)) != null)
                                .map(x => x.TeamID);

                            this.addTeams();
                        }
                    }
                } catch(e) {
                    console.log(e);
                } finally {
                    this.loadingTeamRosterResult = false
                }
            }
        }
    },
    computed: {
        ...mapState("RosterStore", [
            "plantData",
            "selectedPlantID",
            "selectedCategoryIDs",
            "selectedDate",
            "financialYearCalendarData",
            "rosterTeamPlans",
            "cropLocationVarietyRostersRaw",
            "cropLocationVarietyRosters",
            "teamRosterResult",
            "localTeamIDs",
            "selectedUOM"
        ]),
         ...mapGetters("RosterStore", [
            "selectedTeamIDs",
            "teams",
            "days",
            "daysWithoutPreviousSunday",
            "startOfSelectedWeek",
            "endOfSelectedWeek",
            "selectedUOMScaleFactors"
        ]),
        plants(){
            return this.plantData.map(x => {
                return {
                    text: x.PlantName,
                    value: x.PlantID
                };
            }).sort(UtilService.sortAscending(x => x.text));
        },
        categories(){
            let plant = this.plantData.find(x => x.PlantID === this.selectedPlantID);
            if(plant == null){
                return [];
            }

            return plant.ProductCategories.map(x => {
                return {
                    text: x.ProductCategoryName,
                    value: x.ProductCategoryID
                };
            }).sort(UtilService.sortAscending(x => x.text));
        },
        selectedTeams() {
            var teamIDs = this.selectedTeamIDs.concat(this.localSelectedTeamIDs);
            console.log(this.selectedTeamIDs.concat(this.localSelectedTeamIDs))
            return this.teams.filter(t=> teamIDs.indexOf(t.TeamID) >= 0 && this.localDeletedTeamIDs.indexOf(t.TeamID) < 0);
         },
        teamItems() {
            // It will show the selected team with value rostered for the selected product category
            // Plus the local new added team with might not have any rosters 
            var teamIDs = this.selectedTeamIDs.concat(this.localSelectedTeamIDs).concat(this.localTeamIDs);
            var tt =  this.teams
            .filter(t=> this.localDeletedTeamIDs.indexOf(t.TeamID) < 0)
            .filter(t=>{
                var hasRosterForProductCategory = false;
                var result = this.teamRosterResult.find(trr=> trr.TeamID == t.TeamID);
                if(result == null) {
                    hasRosterForProductCategory =  false;
                } else if(result.ProductCategories.length == 0) {
                   hasRosterForProductCategory = false;
                } else {
                  hasRosterForProductCategory = result.ProductCategories.find(pc=> this.selectedCategoryIDs.some(spt=> spt == pc.ProductCategoryID)) != null;
                }
                return teamIDs.indexOf(t.TeamID) >= 0
                && (hasRosterForProductCategory || this.localSelectedTeamIDs.concat(this.localTeamIDs).indexOf(t.TeamID) >= 0);
            });
            return tt;
        },
        
        unselectedTeams() {
            return this.teams
            .filter(t=> this.localSelectedTeamIDs.concat(this.localTeamIDs).indexOf(t.TeamID) < 0)
            .filter(t=> {
                var noRosterForProductCategory = false;
                var result = this.teamRosterResult.find(trr=> trr.TeamID == t.TeamID);
                if(result == null) {
                    noRosterForProductCategory =  true;
                } else if(result.ProductCategories.length == 0) {
                    noRosterForProductCategory = true;
                } else {
                    noRosterForProductCategory = result.ProductCategories.find(pc=> this.selectedCategoryIDs.some(spt=> spt == pc.ProductCategoryID)) == null;
                }
                return (this.selectedTeamIDs.indexOf(t.TeamID) < 0) || noRosterForProductCategory;

            });
        },
        plantSelected(){
            return this.selectedPlantID != null;
        },
        categorySelected(){
            return this.selectedCategoryIDs.length > 0;
        },
        financialWeekSelected() {
            return this.selectedDate != null;
        },
        financialWeekDescription(){
            var financialWeek = RosterUtilService.getFinancialWeek(this.selectedDate, this.financialYearCalendarData);
            return `Active WEEK ${financialWeek}`;
        },
        dateRangeDescription() {
            return `${moment(this.selectedDate).startOf('week').format('DD MMMM YYYY')} - ${moment(this.selectedDate).endOf('week').format('DD MMMM YYYY')}`;
        },
        canSelectUOM() {
            return this.categorySelected;
        }
    },
    methods: {
        ...mapMutations("RosterStore", [
            "setSelectedPlantID",
            "setSelectedCategoryIDs",
            "setSelectedDate",
            "setShowPreviousSunday",
            "setShowActuals",
            "setShowMultiSelect",
            "setFilterUOM"
        ]),
        ...mapActions("RosterStore", [
            "loadWeatherData",
            "loadCropLocationVarietyRosterData",
            "loadTeamRosterResult",
            "addLocalTeamIDs",
            "removeLocalTeamIDs",
            "mapCropLocationVarietyRosters"
        ]),
        async handlePlantSelected(value){
            this.setSelectedPlantID(value);
            if(this.selectedPlantID != null) {
                // Weather data is not used in any subsequent data so it can be loaded separately
                this.loadWeatherData();
            }
            this.$nextTick(() => {
                if(this.categories.length == 1){
                    this.handleCategorySelected([this.categories[0].value])
                } else if(!this.categories.some(x => this.selectedCategoryIDs.indexOf(x.value) >= 0)){
                    this.handleCategorySelected([]);
                } else {
                    this.handleCategorySelected(this.selectedCategoryIDs)
                }
            });
        },
        handleCategorySelected(value){
            this.setSelectedCategoryIDs(value);
            if(this.selectedPlantID != null && this.selectedCategoryIDs.length > 0 && this.selectedDate != null) {
                this.$emit("loadCropLocationVarietyRosterData");
            }
        },
        handleTeamSelected(value) {
            console.log('team selected', value)
            this.tempSelectedTeamIDs = value;
        },
        async handleUOMSelected(value) {
            this.setFilterUOM(value);
            await this.mapCropLocationVarietyRosters();
        },
        addTeams() {
            this.localSelectedTeamIDs = [... new Set(this.localSelectedTeamIDs.concat(this.tempSelectedTeamIDs))];
            this.$nextTick(()=> {
                this.tempSelectedTeamIDs = [];
            })
            UtilService.focusRefElement(this.$refs, 'teamRosterButtonAdd');
        },
        deallocateTeam(teamID) {
            if(this.localSelectedTeamIDs.indexOf(teamID) >= 0) {
                this.localSelectedTeamIDs.splice(this.localSelectedTeamIDs.indexOf(teamID), 1);
            } else {
                this.localDeletedTeamIDs.push(teamID);
            }

        },
        hasAllocatedVolumeForTeam(teamID) {
            if(this.teamRosterResult.find(t=> t.TeamID == teamID) == null) {
                return false;
            } else {
                return this.teamRosterResult.find(t=> t.TeamID == teamID).Rostered;
            }
        },
        onDateSelected(date) {
            this.setSelectedDate(date);
            if(this.selectedPlantID != null && this.selectedCategoryIDs.length > 0 && this.selectedDate != null) {
                this.$emit("loadCropLocationVarietyRosterData");
            }
        },
        openAddTeamDialog() {
            this.tempSelectedTeamIDs = [];
            this.localDeletedTeamIDs = [];
            this.localSelectedTeamIDs = [];
            this.addTeamDialog = true;
        },
        confirmTeamSelection() {
            var addRosterTeamPlans = RosterUtilService.generateNewRosterTeamPlans(this.localSelectedTeamIDs, this.daysWithoutPreviousSunday);
            this.addLocalTeamIDs(this.localSelectedTeamIDs);
            var deleteRoserTeamPlans = RosterUtilService.generateDeleteRosterTeamPlans(this.localDeletedTeamIDs, this.rosterTeamPlans);
            this.removeLocalTeamIDs(this.localDeletedTeamIDs);

            this.$emit("updateTeamRosterPlans", addRosterTeamPlans.concat(deleteRoserTeamPlans));
            this.closeAddTeamDialog();
        },
        closeAddTeamDialog() {
            this.addTeamDialog = false;
            UtilService.focusRefElement(this.$refs, 'teamRosterButton');
        },
        showPreviousSundayChanged() {
            this.setShowPreviousSunday(this.showPreviousSunday);

            if(this.selectedPlantID != null && this.selectedCategoryIDs.length > 0 && this.selectedDate != null) {
                this.$emit("loadCropLocationVarietyRosterData");
            }
        },
        showActualsChanged() {
            this.setShowActuals(this.showActuals);

            if(this.selectedPlantID != null && this.selectedCategoryIDs.length > 0 && this.selectedDate != null) {
                this.$emit("loadCropLocationVarietyRosterData");
            }
        },
        showMultiSelectChanged() {
            this.setShowMultiSelect(this.showMultiSelect);
        }
    },
    created(){
        if(this.plants.length == 1){
            this.handlePlantSelected(this.plants[0].value)
        } else if(!this.plants.some(x => x.value == this.selectedPlantID)){
            this.handlePlantSelected(null);
        }
    }
}
</script>

<style>
    .rosterTeamSelect .v-input__append-outer {
        margin: auto!important;
    }
</style>